import config from './config';
import {
  FluviusEan,
  FluviusMandatesResponse,
  CreateMandateLinkRequest,
  CreateMandateLinkResponse,
  SendMandateLinkRequest,
  SendMandateLinkResponse,
} from '../types/mandateTypes';

export type { FluviusEan };

export class FluviusMandatesError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'FluviusMandatesError';
    this.status = status;
  }
}

/**
 * Thrown when POST /fluvius/mandates/send-link answers 500 because EVERY recipient
 * failed. The backend still sends the full { sent: 0, failed: N, results: [...] } body
 * in that case, so this carries the per-recipient detail instead of collapsing it into
 * a bare "500" message. Extends Error, so callers that only read `.message` are
 * unaffected.
 */
export class SendMandateLinkAllFailedError extends Error {
  response: SendMandateLinkResponse;

  constructor(response: SendMandateLinkResponse) {
    super(`Failed to send mandate link: all ${response.failed} recipient(s) failed`);
    this.name = 'SendMandateLinkAllFailedError';
    this.response = response;
  }
}

const isSendMandateLinkResponse = (data: unknown): data is SendMandateLinkResponse =>
  !!data &&
  typeof data === 'object' &&
  typeof (data as SendMandateLinkResponse).sent === 'number' &&
  typeof (data as SendMandateLinkResponse).failed === 'number' &&
  Array.isArray((data as SendMandateLinkResponse).results);

export const fetchFluviusMandates = async (companyId: number, token: string): Promise<FluviusMandatesResponse> => {
  const url = `${config.API_SERVICES}/api/v1/fluvius/mandates?companyId=${companyId}`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new FluviusMandatesError(response.status, `Failed to fetch Fluvius mandates: ${response.status}`);
  }

  return response.json();
};

export const createMandateLink = async (body: CreateMandateLinkRequest, token: string): Promise<CreateMandateLinkResponse> => {
  const url = `${config.API_SERVICES}/api/v1/fluvius/mandates/link`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(data.error || `Failed to create mandate link: ${response.status}`);
  }

  return response.json();
};

export const sendMandateLink = async (body: SendMandateLinkRequest, token: string): Promise<SendMandateLinkResponse> => {
  const url = `${config.API_SERVICES}/api/v1/fluvius/mandates/send-link`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({ error: 'Unknown error' }));
    // An all-recipients-failed 500 carries the same { sent, failed, results } body as a
    // 200 (see fluviusHandler.sendMandateLink). Preserve it so the UI can name the
    // addresses that bounced; a generic 500 ({ error: ... }) still throws plainly.
    if (isSendMandateLinkResponse(data)) {
      throw new SendMandateLinkAllFailedError(data);
    }
    throw new Error(data.error || `Failed to send mandate link: ${response.status}`);
  }

  return response.json();
};
